import { useRef, useEffect, useState } from "react";
import logger from "../utils/logger";

interface UseStreamVideoOptions {
  streamType?: "screen" | "webcam";
}

const useStreamVideo = (
  stream: MediaStream | null,
  options: UseStreamVideoOptions = {}
) => {
  const { streamType = "webcam" } = options;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    if (!stream) {
      if (videoRef.current) {
        videoRef.current.srcObject = null;
        videoRef.current = null;
      }
      setIsVideoReady(false);
      return;
    }

    // Create video element if it doesn't exist
    if (!videoRef.current) {
      const video = document.createElement("video");
      video.autoplay = true;
      video.muted = true;
      video.playsInline = true;
      video.style.display = "none";
      video.setAttribute("playsinline", "true");
      video.setAttribute("webkit-playsinline", "true");
      document.body.appendChild(video);
      videoRef.current = video;
    }

    const video = videoRef.current;
    if (!video) return;

    // Set up event listeners for better video state management
    const handleLoadedMetadata = () => {
      // Set proper dimensions for Fabric.js
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        video.setAttribute("width", video.videoWidth.toString());
        video.setAttribute("height", video.videoHeight.toString());
        setIsVideoReady(true);
        if (streamType === "screen") {
          logger.info("Screen video dimensions updated:", {
            width: video.videoWidth,
            height: video.videoHeight,
          });
        }
      }

      // Ensure video is playing for FabricImage
      const playPromise = video.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            //logger.info("Video playback started successfully");
          })
          .catch((error) => {
            logger.error("Failed to play video:", error);
            // Try to handle autoplay restrictions
            if (error.name === "NotAllowedError") {
              video.muted = true;
              video.play().catch(() => {
                logger.warn("Video playback failed even with muted audio");
              });
            }
          });
      }
    };

    const handleCanPlay = () => {
      // Video is ready to play
      setIsVideoReady(true);
    };

    const handleError = (error: any) => {
      logger.error("Video element error:", error);
      setIsVideoReady(false);
    };

    // Only apply resize optimizations for screen streams
    const handleResize = () => {
      if (
        streamType === "screen" &&
        video.videoWidth > 0 &&
        video.videoHeight > 0
      ) {
        video.setAttribute("width", video.videoWidth.toString());
        video.setAttribute("height", video.videoHeight.toString());
        // Force a metadata reload to ensure dimensions are updated
        video.dispatchEvent(new Event("loadedmetadata"));
        logger.info("Screen video resize detected, dimensions:", {
          width: video.videoWidth,
          height: video.videoHeight,
        });
      }
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("error", handleError);

    // Only add resize listener for screen streams
    if (streamType === "screen") {
      video.addEventListener("resize", handleResize);
    }

    // Set up ResizeObserver only for screen streams to detect dimension changes
    if (streamType === "screen" && window.ResizeObserver) {
      resizeObserverRef.current = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (
            entry.target === video &&
            video.videoWidth > 0 &&
            video.videoHeight > 0
          ) {
            // Force immediate dimension update for screen streams
            video.setAttribute("width", video.videoWidth.toString());
            video.setAttribute("height", video.videoHeight.toString());
            video.dispatchEvent(new Event("loadedmetadata"));
            logger.info("Screen ResizeObserver detected dimension change:", {
              width: video.videoWidth,
              height: video.videoHeight,
            });
          }
        }
      });

      resizeObserverRef.current.observe(video);
    }

    // Set the stream source
    if (video.srcObject !== stream) {
      video.srcObject = stream;
    }

    // Cleanup event listeners and observers
    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("canplay", handleCanPlay);
      video.removeEventListener("error", handleError);

      if (streamType === "screen") {
        video.removeEventListener("resize", handleResize);
      }

      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, [stream, streamType]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (videoRef.current) {
        videoRef.current.srcObject = null;
        if (videoRef.current.parentNode) {
          videoRef.current.parentNode.removeChild(videoRef.current);
        }
        videoRef.current = null;
      }
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      setIsVideoReady(false);
    };
  }, []);

  return { videoRef, isVideoReady };
};

export default useStreamVideo;
